import React, { useId, useRef } from 'react'
import { Collapse } from 'react-bootstrap'
import { useLocalStorage, useWindowSize } from 'react-use'
import toNumber from 'lodash/toNumber'
import { BusyButton } from '@ztechapps/webui.button'
import { HorizontalStack, ShowIf, TooltipPopOver, useDragResize } from '@ztechapps/webui.util'
import styles from './CollapsibleSidebar.module.scss'

const SIDEBAR_MIN_WIDTH = 240
// The width a panel opens at until a drag or a key stores one.
const SIDEBAR_DEFAULT_WIDTH_RATIO = 0.25
const SIDEBAR_MAX_WIDTH_RATIO = 0.5
const SIDEBAR_KEYBOARD_STEP = 20

type CollapsibleSidebarProps = {
  sidebar: React.ReactNode
  storageKey: string
  children: React.ReactNode
}

/**
 * The side panel beside a page's main content, pinned under the title band while the page scrolls.
 * It renders a light-grey, full-height container the section cards span edge to edge; one round
 * chip on the container's right edge that collapses it to a full-height "Details" rail (the chip
 * then sits centred on the rail, and a click on the rail expands it too); and a separator on the
 * same edge that the user drags, or steps with the arrow keys, to set the panel's width — a quarter
 * of the window until then, kept between a minimum and half the window.
 *
 * The collapsed flag is stored under `storageKey`, the width under `${storageKey}:width`
 * (derived-key shape follows libs/ai/webui/src/floating-chat-bubble/Context.tsx:64-66).
 */
export const CollapsibleSidebar = ({ sidebar, storageKey, children }: CollapsibleSidebarProps) => {
  const [collapsed, setCollapsed] = useLocalStorage<boolean>(storageKey, false)
  // 0 means never resized.
  // COUPLING: the `${storageKey}:width` key shape must match SidePanelPage.storeWidthAndReload in
  // apps/panelpunches/api-test/src/browser/support/page-objects/admin/SidePanelPage.ts.
  const [storedWidth, setStoredWidth] = useLocalStorage<number>(`${storageKey}:width`, 0)
  const { width: windowWidth } = useWindowSize()
  const panelId = useId()

  // A maximum under the minimum loses — the minimum keeps the panel readable on a narrow window.
  const maxWidth = Math.max(SIDEBAR_MIN_WIDTH, Math.round(windowWidth * SIDEBAR_MAX_WIDTH_RATIO))
  const clampWidth = (value: number) => Math.min(maxWidth, Math.max(SIDEBAR_MIN_WIDTH, value))
  // Rendering clamps; only a drag or a key writes storage.
  // Shape follows the composer splitter: libs/chat/webui/src/Inbox/ConversationDetail.tsx (COMPOSER_KEYBOARD_STEP).
  const panelWidth = clampWidth(toNumber(storedWidth) || Math.round(windowWidth * SIDEBAR_DEFAULT_WIDTH_RATIO))
  const dragStartWidthRef = useRef(panelWidth)

  const { isResizing, onMouseDown } = useDragResize({
    cursor: 'col-resize',
    onStart: () => {
      dragStartWidthRef.current = panelWidth
    },
    onDrag: ({ startX, clientX }) => setStoredWidth(clampWidth(dragStartWidthRef.current + clientX - startX)),
  })

  // Every other key falls through untouched.
  const onSeparatorKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      setStoredWidth(clampWidth(panelWidth - SIDEBAR_KEYBOARD_STEP))
      return
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      setStoredWidth(clampWidth(panelWidth + SIDEBAR_KEYBOARD_STEP))
    }
  }

  return (
    <HorizontalStack gap={2} className={`align-items-start ${styles.collapsibleSidebar}`}>
      <div className={`position-sticky align-self-start d-flex ${styles.sidePanel}`}>
        {/* The animated element carries no padding and its one child a fixed width, so the
            width transition measures the panel and nothing else. */}
        <Collapse in={!collapsed} dimension="width">
          <div id={panelId}>
            <div style={{ width: panelWidth }} className={`overflow-y-auto bg-body-tertiary ${styles.panelScroller}`}>
              {sidebar}
            </div>
          </div>
        </Collapse>
        {/* The whole rail expands on a click, but only for the pointer: the toggle chip is the one
            control for the keyboard and assistive tech, so the rail stays out of the accessibility
            tree and the tab order instead of repeating the chip's name. */}
        <ShowIf condition={!!collapsed}>
          <div
            aria-hidden="true"
            onClick={() => setCollapsed(false)}
            className={`border d-flex justify-content-center bg-body-tertiary ${styles.sideRail}`}
            style={{ width: '2rem' }}
          >
            <div className={`${styles.railLabel} pt-5 text-nowrap`}>Details</div>
          </div>
        </ShowIf>
        <ShowIf condition={!collapsed}>
          <TooltipPopOver tooltipBody="Drag to resize">
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panel"
              aria-controls={panelId}
              aria-valuenow={panelWidth}
              aria-valuemin={SIDEBAR_MIN_WIDTH}
              aria-valuemax={maxWidth}
              tabIndex={0}
              onMouseDown={onMouseDown}
              onKeyDown={onSeparatorKeyDown}
              className={`position-absolute top-0 bottom-0 start-100 ${styles.panelSeparator} ${
                isResizing ? styles.resizing : ''
              }`}
              style={{ width: '0.5rem' }}
            />
          </TooltipPopOver>
        </ShowIf>
        {/* Outside the Collapse subtree, so it stays mounted — and focused — through a toggle:
            Bootstrap's `.collapse:not(.show)` is `display: none`. On the panel's right edge while
            expanded, centred on the rail while collapsed. `bg-body` fills it with the page surface,
            so it stands out against the light-grey (`bg-body-tertiary`) container and rail. */}
        <BusyButton
          variant="link"
          icon={collapsed ? 'angles-right' : 'angles-left'}
          title={collapsed ? 'Expand details' : 'Collapse details'}
          onClick={() => setCollapsed(!collapsed)}
          className={`position-absolute top-0 mt-2 translate-middle-x rounded-circle border shadow-sm bg-body p-0 d-flex align-items-center justify-content-center ${
            collapsed ? 'start-50' : 'start-100'
          }`}
          style={{ width: '1.5rem', height: '1.5rem' }}
        />
      </div>
      <div className="flex-fill" style={{ minWidth: 0 }}>
        {children}
      </div>
    </HorizontalStack>
  )
}
